'use client';

import {
  trackAddToCart,
  trackStartConsultation,
  trackUnbuyableVariantViewed,
} from '@lib/analytics/events';
import { productToGA4Item } from '@lib/analytics/items';
import { COUNTRY_CODE } from '@lib/constants';
import { useToast } from '@lib/context/toast-context';
import { addToCart } from '@lib/data/cart';
import { useIntersection } from '@lib/hooks/use-in-view';
import { useNavigationLoading } from '@lib/hooks/use-navigation-loading';
import type { PharmaVariantMetadata } from '@lib/types/pharma';
import { isVariantInStock } from '@lib/util/inventory';
import { getVariantTitle } from '@lib/util/product';
import { Button } from '@modules/common/components/button';
import { CART_CHANGED_EVENT } from '@modules/common/components/cart-provider/constants';
import Divider from '@modules/common/components/divider';
import InfoNotice from '@modules/products/components/info-notice';
import NotForSaleNotice from '@modules/products/components/not-for-sale-notice';
import PharmaVariantLabel from '@modules/products/components/pharma-variant-label';
import OptionSelect from '@modules/products/components/product-actions/option-select';
import {
  fetchVariantAvailability,
  isStockBlocked,
} from '@modules/products/utils/remaining-stock';
import {
  type CTAState,
  ctaUnavailableReason,
  getCTAState,
  getMetadataBlockKind,
  getUnbuyableVariantEvent,
  isBlockedByMetadata,
} from '@modules/products/utils/requires-consultation';
import {
  optionsAsKeymap,
  sameOptionsKeymap,
} from '@modules/products/utils/variant-options';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProductActionsVM } from 'types/view-models';
import ProductPrice from '../product-price';
import AddErrorBanner from './add-error-banner';
import { BuyNowPayLater } from './buy-now-pay-later-info';
import MobileActions from './mobile-actions';
import OutOfStockCta from './out-of-stock-cta';
import TrustSignals from './trust-signals';

const PMedConsultationModal = dynamic(
  () => import('@modules/consultation/modal/p-med-consultation-modal'),
  { ssr: false },
);

type PMedModalSnapshot = {
  variantId: string;
  quantity: number;
  productHandle: string;
  productTitle: string;
};

type ProductActionsProps = {
  product: ProductActionsVM;
  disabled?: boolean;
};

const MAX_STOCK_MESSAGE =
  'You already have the maximum currently available quantity of this item in your basket.';

// Pre-selects the variant named by the PDP's `?variant=` query param (order
// history links pass the variant title, the Google Product Feed passes the
// SKU) — matches by title first, then falls back to SKU. Read client-side
// via `useSearchParams` (the caller wraps this component in `<Suspense>`) so
// the PDP route itself never touches a Dynamic API and stays eligible for
// the page-level `revalidate`.
function useInitialOptionsFromSearchParam(
  variants: ProductActionsVM['variants'],
): Record<string, string> {
  const searchParams = useSearchParams();
  const requestedParam = searchParams.get('variant') ?? undefined;

  if (!requestedParam) {
    return {};
  }

  const requestedVariant =
    variants?.find((v) => getVariantTitle(v) === requestedParam) ??
    variants?.find((v) => v.sku === requestedParam);

  return requestedVariant
    ? (optionsAsKeymap(requestedVariant.options) ?? {})
    : {};
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: variant selection, consultation flow, and stock gating are inherently branchy state derivations
export default function ProductActions({
  product,
  disabled,
}: ProductActionsProps) {
  const initialOptions = useInitialOptionsFromSearchParam(product.variants);
  const [isAdding, setIsAdding] = useState(false);
  const [options, setOptions] = useState<Record<string, string | undefined>>(
    () => initialOptions,
  );

  const showToast = useToast();
  const { navigate: navigateTo, isPending: isStartingConsultation } =
    useNavigationLoading();

  const [addError, setAddError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const [pMedSnapshot, setPMedSnapshot] = useState<PMedModalSnapshot | null>(
    null,
  );

  // Populated once the mount/focus/visibility effect below resolves, so
  // stock freed up elsewhere (e.g. the cart being edited in another tab)
  // re-enables the CTA without a page refresh.
  const [remainingStockOverride, setRemainingStockOverride] = useState<
    Record<string, number | undefined>
  >({});

  // Populated by the same effect, from the same live fetch — refreshes
  // whether the variant is purchasable at all, so a restock (or a stale
  // globally-cached `inventory_quantity` at render time) is reflected
  // without waiting for the page's own cache TTL to expire. Falls back to
  // the static `isVariantInStock` snapshot below until this resolves.
  const [inStockOverride, setInStockOverride] = useState<
    Record<string, boolean>
  >({});

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options);
      setOptions(variantOptions ?? {});
    }
  }, [product.variants]);

  const selectedVariant = useMemo(() => {
    const variants = product.variants ?? [];
    if (variants.length === 0) {
      return undefined;
    }
    // Single-variant products: the only variant is always "selected" — no
    // option selection UI is rendered for them. Returning it directly here
    // (rather than relying on `isEqual(keymap, options)`) keeps the first
    // render deterministic regardless of whether the fetched `v.options` is
    // `[]` or missing — otherwise the empty-keymap match could flip between
    // SSR and hydrate and produce a hydration mismatch on the CTA button.
    if (variants.length === 1) {
      return variants[0];
    }
    // Multi-variant: only match once the user has actually selected options.
    // An empty `options` state must NOT coincidentally match a variant whose
    // options array serialised to `[]`.
    if (Object.keys(options).length === 0) {
      return undefined;
    }
    return variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options);
      return sameOptionsKeymap(variantOptions, options);
    });
  }, [product.variants, options]);

  const pharmaMetadata = useMemo(() => {
    return selectedVariant?.metadata as PharmaVariantMetadata | undefined;
  }, [selectedVariant]);

  // Defer the "real" CTA until after first commit. PostHog/PII state and
  // browser-side hooks have caused the CTA text to flash between renders in
  // the past — rendering a disabled placeholder until mount means the user
  // never sees an "Add to Basket" → "Start consultation" flip on hydrate.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const ctaState = useMemo<CTAState>(
    () => getCTAState(mounted, selectedVariant),
    [mounted, selectedVariant],
  );
  const consultationMode =
    ctaState.kind === 'consultation' ? ctaState.mode : null;

  const maxQuantity = Math.max(1, pharmaMetadata?.max_sale_quantity ?? 10);

  // Reset quantity when variant changes (in case new max is lower)
  useEffect(() => {
    setQuantity((prev) => Math.min(prev, maxQuantity));
  }, [maxQuantity]);

  useEffect(() => {
    if (selectedVariant?.id) {
      window.dispatchEvent(
        new CustomEvent('medusa:variantchanged', {
          detail: { variantId: selectedVariant.id },
        }),
      );
    }
  }, [selectedVariant?.id]);

  // update the options when a variant is selected
  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [optionId]: value,
    }));
  };

  // `selectedVariant` already does the find — the previous `useMemo` here
  // duplicated the variant scan with identical deps.
  const isValidVariant = !!selectedVariant;

  const inStock = useMemo(() => {
    if (!selectedVariant) {
      return false;
    }
    return (
      inStockOverride[selectedVariant.id] ?? isVariantInStock(selectedVariant)
    );
  }, [selectedVariant, inStockOverride]);

  // A variant blocked purely by its metadata (not_for_sale / unclassified)
  // renders a hard-disabled CTA. Suppress the stock badge in that case so the
  // page can't say "In Stock" next to a button that will never add to basket.
  // Derived from the variant directly (not the mount-gated ctaState) so the
  // badge decision is identical on SSR and after hydrate.
  const blockedByMetadata = useMemo(
    () => isBlockedByMetadata(selectedVariant),
    [selectedVariant],
  );

  // Explanation for a metadata-blocked CTA, shown beneath the button. Derived
  // the same SSR/hydrate-safe way as `blockedByMetadata` (not gated behind
  // `mounted`) so it's present in the initial server-rendered HTML — visible
  // to no-JS users and crawlers, not just after the client hydrates.
  const metadataBlockKind = useMemo(
    () => getMetadataBlockKind(selectedVariant, product.variants),
    [selectedVariant, product.variants],
  );

  const metadataBlockNotice = useMemo(() => {
    if (metadataBlockKind === 'not-for-sale') {
      return <NotForSaleNotice notForSale />;
    }
    if (metadataBlockKind === 'unclassified') {
      const reason = ctaUnavailableReason('unclassified');
      return reason ? <InfoNotice>{reason}</InfoNotice> : null;
    }
    return null;
  }, [metadataBlockKind]);

  // Data hygiene: flag variants whose PDP is live but can't be bought because
  // of missing/incomplete metadata. `missing-questionnaire` (a P/POM product
  // with a classification but no questionnaire_id) is the lost-sale case worth
  // watching; `not_for_sale` is usually a deliberate block. Fires once per
  // variant view so the data team can find products stuck unbuyable by mistake.
  // The payload (and the decision to fire) is built by a pure, unit-tested
  // helper so this component keeps only the effect wiring.
  const unbuyableEvent = useMemo(
    () => getUnbuyableVariantEvent(selectedVariant, product.handle),
    [selectedVariant, product.handle],
  );

  // Guards against React Strict Mode's dev-only mount→effect→cleanup→effect
  // cycle firing this twice for the same variant. Keyed by variant+reason
  // (not a one-shot `fired` flag, unlike the analytics trackers this pattern
  // is based on) so a genuine switch to a different unbuyable variant still
  // fires, and reset to null once the selection clears so re-selecting the
  // same blocked variant later fires again instead of being treated as a
  // repeat.
  const lastFiredUnbuyableEventKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!unbuyableEvent) {
      lastFiredUnbuyableEventKeyRef.current = null;
      return;
    }
    const eventKey = `${unbuyableEvent.variant_id}:${unbuyableEvent.reason}`;
    if (lastFiredUnbuyableEventKeyRef.current === eventKey) {
      return;
    }
    lastFiredUnbuyableEventKeyRef.current = eventKey;
    trackUnbuyableVariantViewed(unbuyableEvent);
  }, [unbuyableEvent]);

  // Re-fetch remaining stock for the selected variant whenever it changes,
  // whenever the tab regains focus, and whenever a cart item is removed
  // elsewhere on the page (e.g. the header's mini-cart popup) — that removal
  // doesn't change window focus/visibility, so it needs its own trigger to
  // re-enable a max-stock-blocked CTA immediately.
  useEffect(() => {
    const variantId = selectedVariant?.id;
    if (!variantId) {
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      try {
        const { remaining, inStock: freshInStock } =
          await fetchVariantAvailability(variantId);
        if (!cancelled) {
          setRemainingStockOverride((prev) => ({
            ...prev,
            [variantId]: remaining,
          }));
          setInStockOverride((prev) => ({
            ...prev,
            [variantId]: freshInStock,
          }));
        }
      } catch {
        // A transient failure here must not leave the CTA stuck on a stale
        // snapshot forever — the next focus/visibility/cart-change trigger
        // gets another chance.
      }
    };

    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('medusa:cartitemremoved', refresh);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('medusa:cartitemremoved', refresh);
    };
  }, [selectedVariant?.id]);

  // Re-fetches remaining stock for a variant right after a successful
  // add-to-cart and writes it into the override state, so the CTA reflects
  // the new "already in basket" ceiling immediately instead of waiting for
  // the variant to change or the tab to regain focus (see the effect above).
  // Best-effort: a transient failure here must not break the add-to-cart
  // success path (toast/analytics/modal state) — the focus/visibility effect
  // above will retry.
  const refreshRemainingStockAfterAdd = async (variantId: string) => {
    try {
      const { remaining, inStock: freshInStock } =
        await fetchVariantAvailability(variantId);
      setRemainingStockOverride((prev) => ({
        ...prev,
        [variantId]: remaining,
      }));
      setInStockOverride((prev) => ({
        ...prev,
        [variantId]: freshInStock,
      }));
    } catch {
      // Ignore — see comment above.
    }
  };

  // Looked up from the client override once it resolves. `undefined` means
  // "no stock-based ceiling"; the submit handlers below re-check at click
  // time as a backstop against staleness between the last refresh and the
  // click. Until the override for this variant exists at all, its stock
  // ceiling is unknown rather than "unlimited" — `stockCheckPending` keeps
  // every CTA disabled for that brief window instead of momentarily
  // allowing a click a stale ceiling would have blocked (the full-page
  // consultation CTA has no click-time backstop of its own, so this is its
  // only guard).
  let remainingStock: number | undefined;
  let stockCheckPending = false;
  if (selectedVariant?.id) {
    if (selectedVariant.id in remainingStockOverride) {
      remainingStock = remainingStockOverride[selectedVariant.id];
    } else {
      stockCheckPending = true;
    }
  }

  const stockBlocked = isStockBlocked(remainingStock, quantity);

  // Precomputed here (rather than inline in renderCTA) to keep renderCTA's
  // own branching limited to picking which CTA to show, not re-deriving why
  // each one is disabled.
  const gslDisabled =
    !!disabled ||
    isAdding ||
    !isValidVariant ||
    stockBlocked ||
    stockCheckPending;
  const modalDisabled =
    !!disabled ||
    isAdding ||
    !isValidVariant ||
    !inStock ||
    stockBlocked ||
    stockCheckPending;
  const fullPageDisabled =
    !!disabled ||
    isAdding ||
    !isValidVariant ||
    stockCheckPending ||
    !selectedVariant ||
    !inStock ||
    stockBlocked;

  const actionsRef = useRef<HTMLDivElement>(null);

  const inView = useIntersection(actionsRef, '0px', true);

  // add the selected variant to the cart
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null;

    setIsAdding(true);
    setAddError(null);

    try {
      const { remaining } = await fetchVariantAvailability(selectedVariant.id);
      if (isStockBlocked(remaining, quantity)) {
        setAddError(MAX_STOCK_MESSAGE);
        return;
      }

      const result = await addToCart({
        variantId: selectedVariant.id,
        quantity,
        countryCode: COUNTRY_CODE,
      });

      if (result?.error) {
        setAddError(result.error);
      } else {
        window.dispatchEvent(new Event(CART_CHANGED_EVENT));
        await refreshRemainingStockAfterAdd(selectedVariant.id);
        if (window.innerWidth < 1024) {
          showToast('Item added to your basket');
        }
        const item = productToGA4Item(product, {
          variantId: selectedVariant.id,
        });
        const price = (item.price ?? 0) * quantity;
        const currency = item.currency ?? 'GBP';
        trackAddToCart({ ...item, quantity }, price, currency);
      }
    } finally {
      setIsAdding(false);
    }
  };

  // Called by the modal when Formulate approves the consultation. Runs the
  // same addToCart path GSL uses, but attaches the consultation IDs to the
  // line metadata so the order can be audited later.
  // Uses the snapshot captured at modal-open time so the variant can never
  // drift if the shopper changes options while the modal is displayed.
  const handleConsultationApproved = async (
    submissionId: string,
    questionnaireId: string,
    photosSkipped: boolean,
  ): Promise<{ error?: string } | undefined> => {
    const snap = pMedSnapshot;
    if (!snap) {
      return { error: 'Consultation session lost.' };
    }
    setIsAdding(true);
    try {
      const { remaining } = await fetchVariantAvailability(snap.variantId);
      if (isStockBlocked(remaining, snap.quantity)) {
        return { error: MAX_STOCK_MESSAGE };
      }

      const result = await addToCart({
        variantId: snap.variantId,
        quantity: snap.quantity,
        countryCode: COUNTRY_CODE,
        metadata: {
          consultation_submission_id: submissionId,
          consultation_questionnaire_id: questionnaireId,
          ...(photosSkipped && { consultation_photos_pending: true }),
        },
      });
      if (result?.error) {
        return { error: result.error };
      }
      window.dispatchEvent(new Event(CART_CHANGED_EVENT));
      await refreshRemainingStockAfterAdd(snap.variantId);
      if (window.innerWidth < 1024) {
        showToast('Item added to your basket');
      }
      const item = productToGA4Item(product, { variantId: snap.variantId });
      const price = (item.price ?? 0) * snap.quantity;
      const currency = item.currency ?? 'GBP';
      trackAddToCart({ ...item, quantity: snap.quantity }, price, currency);
      return undefined;
    } finally {
      setIsAdding(false);
    }
  };

  const navigateToConsultation = (url: string) => {
    handleStartConsultation();
    navigateTo(url);
  };

  // Track when a user initiates the consultation flow for a P/POM product.
  // Navigation is handled by the link — this just fires the analytics event.
  const handleStartConsultation = () => {
    if (!selectedVariant?.id) {
      return;
    }

    const item = productToGA4Item(product, { variantId: selectedVariant.id });
    const price = (item.price ?? 0) * quantity;
    const currency = item.currency ?? 'GBP';
    const classification = pharmaMetadata?.legal_classification ?? 'P';
    const meta = selectedVariant.metadata as PharmaVariantMetadata | undefined;
    trackStartConsultation(
      { ...item, quantity },
      price,
      currency,
      classification,
      meta?.questionnaire_id,
    );
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: CTA branches over the five mutually exclusive ctaState kinds
  const renderCTA = () => {
    if (ctaState.kind === 'mounting') {
      // `metadataBlockNotice` isn't gated behind `mounted` (see its own
      // comment) so a not-for-sale/unclassified explanation is already in
      // the server-rendered HTML — only the button label waits for mount.
      return <UnavailableCta label="Loading…" notice={metadataBlockNotice} />;
    }
    if (ctaState.kind === 'no-variant') {
      // When every variant is blocked the same way (see
      // `getMetadataBlockKind`), prompting "Select option" would contradict
      // the notice — no pick can make the product buyable, so show the block
      // label up front, matching what any selection would resolve to.
      if (metadataBlockKind === 'not-for-sale') {
        return (
          <UnavailableCta
            label="Not available to purchase"
            notice={metadataBlockNotice}
          />
        );
      }
      if (metadataBlockKind === 'unclassified') {
        return (
          <UnavailableCta
            label="Currently unavailable"
            notice={metadataBlockNotice}
          />
        );
      }
      return (
        <Button
          disabled
          variant="primary"
          size="lg"
          className="w-full"
          data-testid="add-product-button"
        >
          <span translate="no">Select option</span>
        </Button>
      );
    }
    if (ctaState.kind === 'unclassified') {
      return (
        <UnavailableCta
          label="Currently unavailable"
          notice={metadataBlockNotice}
        />
      );
    }
    if (ctaState.kind === 'not-for-sale') {
      return (
        <UnavailableCta
          label="Not available to purchase"
          notice={metadataBlockNotice}
        />
      );
    }
    // The stock check for the selected variant hasn't resolved yet — its
    // real availability is unknown, not "in stock" or "out of stock". Show
    // the same loading state as `mounting` rather than letting a stale
    // snapshot render a wrong CTA (see the `!stockCheckPending` guard around
    // the `OutOfStockCta` branch above, which this mirrors).
    if (stockCheckPending) {
      return <UnavailableCta label="Loading…" notice={metadataBlockNotice} />;
    }
    if (ctaState.kind === 'consultation' && ctaState.mode === 'full_page') {
      if (fullPageDisabled) {
        return (
          <Button
            disabled
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={isAdding}
            data-testid="add-product-button"
          >
            <span translate="no">Start consultation</span>
          </Button>
        );
      }
      return (
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={isStartingConsultation}
          data-testid="add-product-button"
          onClick={() =>
            navigateToConsultation(
              `/consultation/start?product=${encodeURIComponent(
                product.handle ?? '',
              )}&variant=${encodeURIComponent(selectedVariant.id ?? '')}&qty=${quantity}`,
            )
          }
        >
          <span translate="no">Start consultation</span>
        </Button>
      );
    }
    if (ctaState.kind === 'consultation' && ctaState.mode === 'modal') {
      return (
        <Button
          onClick={() => {
            if (!selectedVariant?.id) {
              return;
            }
            setPMedSnapshot({
              variantId: selectedVariant.id,
              quantity,
              productHandle: product.handle ?? '',
              productTitle: product.title ?? '',
            });
            handleStartConsultation();
          }}
          disabled={modalDisabled}
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={isAdding}
          data-testid="add-product-button"
        >
          <span translate="no">Add to Basket</span>
        </Button>
      );
    }
    // ctaState.kind === 'gsl' | 'other'
    return (
      <Button
        onClick={handleAddToCart}
        disabled={gslDisabled}
        variant="primary"
        size="lg"
        className="w-full"
        isLoading={isAdding}
        data-testid="add-product-button"
      >
        <span translate="no">Add to Basket</span>
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-y-4">
      {(product.variants?.length ?? 0) > 1 && (
        <div>
          <div className="flex flex-col gap-y-4">
            {(product.options || []).map((option) => {
              return (
                <div key={option.id}>
                  <OptionSelect
                    option={option}
                    current={options[option.id]}
                    updateOption={setOptionValue}
                    title={option.title ?? ''}
                    data-testid="product-options"
                    disabled={!!disabled || isAdding}
                  />
                </div>
              );
            })}
            <Divider />
          </div>
        </div>
      )}

      {selectedVariant && pharmaMetadata && (
        <PharmaVariantLabel
          metadata={pharmaMetadata}
          fallbackTitle={selectedVariant.title ?? undefined}
        />
      )}

      <ProductPrice
        product={product}
        variant={selectedVariant}
        inStock={inStock}
        showStockBadge={!blockedByMetadata}
      />

      <div ref={actionsRef}>
        {selectedVariant && !stockCheckPending && !inStock ? (
          <OutOfStockCta />
        ) : (
          renderCTA()
        )}
      </div>

      <BuyNowPayLater product={product} selectedVariant={selectedVariant} />

      {((inStock && stockBlocked) || addError) && (
        <AddErrorBanner
          message={
            inStock && stockBlocked ? MAX_STOCK_MESSAGE : (addError as string)
          }
        />
      )}

      <TrustSignals />

      <MobileActions
        product={product}
        variant={selectedVariant}
        options={options}
        updateOptions={setOptionValue}
        inStock={inStock}
        showStockBadge={!blockedByMetadata}
        stockBlocked={stockBlocked}
        stockCheckPending={stockCheckPending}
        handleAddToCart={handleAddToCart}
        isAdding={isAdding}
        show={!inView}
        optionsDisabled={!!disabled || isAdding}
        ctaState={ctaState}
        consultationUrl={
          consultationMode === 'full_page' && selectedVariant?.id
            ? `/consultation/start?product=${encodeURIComponent(
                product.handle ?? '',
              )}&variant=${encodeURIComponent(selectedVariant.id)}&qty=${quantity}`
            : undefined
        }
        onStartConsultation={handleStartConsultation}
        onNavigateToConsultation={navigateToConsultation}
        isStartingConsultation={isStartingConsultation}
        onOpenConsultationModal={() => {
          if (!selectedVariant?.id) {
            return;
          }
          setPMedSnapshot({
            variantId: selectedVariant.id,
            quantity,
            productHandle: product.handle ?? '',
            productTitle: product.title ?? '',
          });
        }}
      />

      {pMedSnapshot && (
        <PMedConsultationModal
          isOpen={true}
          onClose={() => setPMedSnapshot(null)}
          onApproved={handleConsultationApproved}
          productHandle={pMedSnapshot.productHandle}
          productTitle={pMedSnapshot.productTitle}
          variantId={pMedSnapshot.variantId}
          quantity={pMedSnapshot.quantity}
          countryCode={COUNTRY_CODE}
        />
      )}
    </div>
  );
}

/**
 * Disabled CTA paired with an optional metadata-block explanation
 * (not_for_sale / unclassified), so an in-stock product never dead-ends with
 * a greyed-out button and no reason. Also used pre-mount and pre-selection,
 * where the notice still renders when every variant is blocked the same way.
 */
function UnavailableCta({
  label,
  notice,
}: {
  label: string;
  notice: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-y-3">
      <Button
        disabled
        variant="primary"
        size="lg"
        className="w-full"
        data-testid="add-product-button"
      >
        <span translate="no">{label}</span>
      </Button>
      {notice}
    </div>
  );
}
